/**
 * Integrytas Group - Common Security Module (CSM)
 * ToastContainer — fixed-position non-blocking alert stack.
 *
 * SUCCESS / INFO auto-dismiss (configured in use_notifications.ts).
 * WARNING / ERROR stay until the user clicks ×.
 */

import { useTranslation } from 'react-i18next';
import type { Toast, NotificationType } from '../../lib/use_notifications';

interface ToastContainerProps {
  toasts:      Toast[];
  onDismiss:   (toastId: string) => void;
}

const STYLES: Record<NotificationType, {
  bar: string; bg: string; text: string; icon: string;
}> = {
  SUCCESS: { bar: 'bg-csm-success',         bg: 'bg-csm-success-soft',  text: 'text-csm-success', icon: '✓' },
  INFO:    { bar: 'bg-csm-info',             bg: 'bg-csm-info-soft',     text: 'text-csm-info',    icon: 'ℹ' },
  WARNING: { bar: 'bg-csm-warning',          bg: 'bg-csm-warning-soft',  text: 'text-csm-warning', icon: '⚠' },
  ERROR:   { bar: 'bg-csm-danger',           bg: 'bg-csm-danger-soft',   text: 'text-csm-danger',  icon: '✕' },
  SYSTEM:  { bar: 'bg-primary',              bg: 'bg-primary-soft',      text: 'text-primary',     icon: '⚙' },
};

export function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  const { t } = useTranslation();

  if (toasts.length === 0) return null;

  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 w-80"
    >
      {toasts.map(toast => {
        const s = STYLES[toast.notification_type] ?? STYLES.INFO;
        return (
          <div
            key={toast.toast_id}
            role="alert"
            className={`flex items-start overflow-hidden rounded border border-csm-border
                        shadow-lg ${s.bg}`}
          >
            {/* Colour bar */}
            <div className={`w-1 shrink-0 self-stretch ${s.bar}`} />

            {/* Content */}
            <div className="flex-1 px-3 py-2.5">
              <div className={`flex items-center gap-1.5 text-csm-sm font-semibold ${s.text}`}>
                <span>{s.icon}</span>
                <span>{toast.title}</span>
              </div>
              {toast.body && (
                <p className="mt-0.5 text-csm-xs text-csm-ink-soft">{toast.body}</p>
              )}
            </div>

            {/* Dismiss */}
            <button
              type="button"
              onClick={() => onDismiss(toast.toast_id)}
              aria-label={t('common.close')}
              className="px-2 py-2 text-csm-muted hover:text-csm-ink text-csm-md leading-none"
            >
              ×
            </button>
          </div>
        );
      })}
    </div>
  );
}
